import { useEffect, useState } from "react";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useNotification from "../../components/notifications/useNotifications";
import useFunctions, { FailedLead, Step8Quote } from "../../data/useFunctions";
import { useQueryClient } from "@tanstack/react-query";
import LoadingWheel from "../../components/shared/LoadingWheel";
import Modal from "../../layouts/Modal";
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  DocumentMagnifyingGlassIcon,
  QueueListIcon,
  MagnifyingGlassCircleIcon,
} from "@heroicons/react/24/outline";
import TraceVrmCard from "./TraceVrm";
import QuoteOwnerPill from "../../components/shared/Quotes/QuoteOwnerPill";

function FunctionCard({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-white rounded-xl shadow p-6 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <div className="bg-primary-50 rounded-lg p-2">
          <Icon className="h-6 w-6 text-primary-600" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function RequeueCard() {
  const { requeuePreview, requeueToday } = useFunctions();
  const { addNotification } = useNotification();
  const [loadingOpen, setLoadingOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<number | null>(null);

  const quotes = requeuePreview.data?.data ?? [];

  const handlePreview = async () => {
    setResult(null);
    setLoadingOpen(true);
    const res = await requeuePreview.refetch();
    setLoadingOpen(false);
    const fetched = res.data?.data ?? [];
    setSelected(new Set(fetched.map((q) => q.id)));
    setModalOpen(true);
  };

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === quotes.length ? new Set() : new Set(quotes.map((q) => q.id))
    );
  };

  const handleConfirm = () => {
    requeueToday.mutate([...selected], {
      onSuccess: (res) => {
        setModalOpen(false);
        setResult(res.data);
        addNotification({ variant: "success", primaryText: res.message ?? `Requeued ${res.data} quote(s)` });
      },
      onError: () => {
        addNotification({ variant: "error", primaryText: "Failed to requeue quotes" });
      },
    });
  };

  return (
    <>
      {/* Loading modal */}
      <Modal open={loadingOpen} setOpen={setLoadingOpen} width="sm:max-w-xs">
        <div className="flex flex-col items-center gap-3 py-4">
          <LoadingWheel />
          <p className="text-sm text-gray-500">Loading quotes…</p>
        </div>
      </Modal>

      {/* Requeuing modal */}
      <Modal open={requeueToday.isLoading} setOpen={() => {}} width="sm:max-w-xs">
        <div className="flex flex-col items-center gap-3 py-4">
          <LoadingWheel />
          <p className="text-sm text-gray-500">Requeuing {selected.size} quote(s)…</p>
        </div>
      </Modal>

      {/* Preview / confirm modal */}
      <Modal open={modalOpen} setOpen={setModalOpen} width="sm:max-w-2xl">
        <div className="flex flex-col gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Requeue Today's Quotes</h3>
            <p className="mt-1 text-sm text-gray-500">
              {selected.size} of {quotes.length} quote(s) selected.
            </p>
          </div>

          {quotes.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">No quotes to requeue.</p>
          ) : (
            <div className="max-h-80 overflow-y-auto rounded-md border border-gray-200">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selected.size === quotes.length}
                        onChange={toggleAll}
                        className="rounded border-gray-300 text-primary-600"
                      />
                    </th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">VRM</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">Contact</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">Postcode</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">Step</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-500">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {quotes.map((q) => (
                    <tr
                      key={q.id}
                      onClick={() => toggleRow(q.id)}
                      className={`cursor-pointer hover:bg-gray-50 ${!selected.has(q.id) ? "opacity-40" : ""}`}
                    >
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selected.has(q.id)}
                          onChange={() => toggleRow(q.id)}
                          className="rounded border-gray-300 text-primary-600"
                        />
                      </td>
                      <td className="px-3 py-2 font-mono font-medium text-gray-900">{q.vehicleRegistration ?? "—"}</td>
                      <td className="px-3 py-2 text-gray-600">
                        <div>{q.contactName || "—"}</div>
                        <div className="text-gray-400">{q.contactPhone || ""}</div>
                      </td>
                      <td className="px-3 py-2 text-gray-600">{q.postcode ?? "—"}</td>
                      <td className="px-3 py-2 text-gray-600">{q.step}</td>
                      <td className="px-3 py-2 text-gray-400">
                        {new Date(q.createdDate).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={() => setModalOpen(false)}
              className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={selected.size === 0}
              className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
            >
              Requeue {selected.size > 0 ? `${selected.size} ` : ""}Quote(s)
            </button>
          </div>
        </div>
      </Modal>

      <div className="flex flex-col gap-3">
        <p className="text-sm text-gray-600">
          Finds all SAV quotes created today that have a contact but haven't been converted to a lead, and adds them to the call centre queue.
        </p>
        {result !== null && (
          <p className="text-sm font-medium text-green-700 bg-green-50 rounded-md px-3 py-2">
            {result} quote(s) requeued
          </p>
        )}
        <button
          onClick={handlePreview}
          disabled={requeuePreview.isFetching}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
        >
          <ArrowPathIcon className="h-4 w-4" />
          Requeue Today's Quotes
        </button>
      </div>
    </>
  );
}

function FailedLeadsCard() {
  const { failedLeads, resendLead } = useFunctions();
  const { addNotification } = useNotification();

  const handleResend = (lead: FailedLead) => {
    resendLead.mutate(lead.id, {
      onSuccess: () => {
        addNotification({ variant: "success", primaryText: `Lead for ${lead.vehicleRegistration} resent successfully` });
      },
      onError: () => {
        addNotification({ variant: "error", primaryText: `Failed to resend lead for ${lead.vehicleRegistration}` });
      },
    });
  };

  if (failedLeads.isLoading) return <LoadingWheel />;

  const leads = failedLeads.data?.data ?? [];

  if (leads.length === 0) {
    return <p className="text-sm text-gray-500">No failed leads.</p>;
  }

  const usedUp = leads.filter((l) => l.retriesUsedUp && l.wilmaProConfigured).length;
  const notOnPro = leads.filter((l) => !l.wilmaProConfigured).length;

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-gray-500">
        {leads.length} lead(s) · {usedUp} no longer retried automatically
        {notOnPro > 0 && ` · ${notOnPro} for customers without Wilma Pro`}
      </p>
      <div className="overflow-x-auto max-h-96 overflow-y-auto rounded-md border border-gray-100">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-50 sticky top-0">
            <tr className="border-b border-gray-100">
              <th className="text-left px-3 py-2 font-medium text-gray-500">VRM</th>
              <th className="text-left px-3 py-2 font-medium text-gray-500">Customer</th>
              <th className="text-left px-3 py-2 font-medium text-gray-500">Last Failed</th>
              <th className="text-left px-3 py-2 font-medium text-gray-500">Attempts</th>
              <th className="text-left px-3 py-2 font-medium text-gray-500">Error</th>
              <th className="text-left px-3 py-2 font-medium text-gray-500"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50 bg-white">
            {leads.map((lead) => (
              <tr key={lead.id} className={!lead.wilmaProConfigured ? "opacity-60" : ""}>
                <td className="px-3 py-2 font-mono font-medium text-gray-900">
                  <div>{lead.vehicleRegistration ?? "—"}</div>
                  <div className="font-sans font-normal text-gray-400">{lead.contactName || ""}</div>
                </td>
                <td className="px-3 py-2">
                  {lead.customerId ? <QuoteOwnerPill customerId={lead.customerId} showLabel={false} /> : "—"}
                </td>
                <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                  {new Date(lead.updatedDate).toLocaleString("en-GB", {
                    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
                  })}
                </td>
                <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                  {lead.sendAttempts}
                  {!lead.wilmaProConfigured ? (
                    <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">Not on Wilma Pro</span>
                  ) : lead.retriesUsedUp ? (
                    <span className="ml-2 rounded-full bg-amber-50 px-2 py-0.5 text-amber-800">Retries used up</span>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-gray-600 max-w-xs">
                  <span className="line-clamp-2 break-words" title={lead.lastSendError ?? undefined}>
                    {lead.lastSendError ?? <span className="text-gray-400">Not recorded</span>}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-1">
                    <button
                      onClick={() => window.open(`/leads/${lead.id}`, "_blank")}
                      className="rounded bg-gray-100 px-2 py-1 text-xs text-gray-700 hover:bg-gray-200"
                    >
                      View
                    </button>
                    {lead.wilmaProConfigured && (
                      <button
                        onClick={() => handleResend(lead)}
                        disabled={resendLead.isLoading}
                        className="rounded bg-primary-600 px-2 py-1 text-xs text-white hover:bg-primary-700 disabled:opacity-60"
                      >
                        Resend
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Step8Card() {
  const { step8Quotes } = useFunctions();

  if (step8Quotes.isLoading) return <LoadingWheel />;

  const quotes = step8Quotes.data?.data ?? [];

  if (quotes.length === 0) {
    return <p className="text-sm text-gray-500">No step 8 quotes without leads.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="border-b border-gray-100">
            <th className="text-left py-2 pr-3 font-medium text-gray-500">VRM</th>
            <th className="text-left py-2 pr-3 font-medium text-gray-500">Contact</th>
            <th className="text-left py-2 pr-3 font-medium text-gray-500">Postcode</th>
            <th className="text-left py-2 pr-3 font-medium text-gray-500">Step</th>
            <th className="text-left py-2 font-medium text-gray-500">Updated</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {quotes.map((quote) => (
            <tr
              key={quote.id}
              className="hover:bg-gray-50 cursor-pointer"
              onClick={() => window.open(`/sellavehicle/${quote.id}`, "_blank")}
            >
              <td className="py-2 pr-3 font-mono font-medium text-gray-900">{quote.vehicleRegistration ?? "—"}</td>
              <td className="py-2 pr-3 text-gray-600">
                <div>{quote.contactName || "—"}</div>
                <div className="text-gray-400">{quote.contactPhone || ""}</div>
              </td>
              <td className="py-2 pr-3 text-gray-600">{quote.postcode ?? "—"}</td>
              <td className="py-2 pr-3 text-gray-600">{quote.furthestStep}</td>
              <td className="py-2 text-gray-400">
                {new Date(quote.updatedDate).toLocaleDateString("en-GB")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QueueCard() {
  const { queueStatus } = useFunctions();
  const queryClient = useQueryClient();

  const data = queueStatus.data?.data;
  const quotes = data?.quotes ?? [];
  const count = data?.approximateCount ?? 0;

  const handleRefresh = () => {
    queryClient.invalidateQueries(["functions-queue-status"]);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">
            {queueStatus.isLoading
              ? "Loading…"
              : `${count} in queue · ${quotes.length} ready now`}
          </span>
          {count > 32 && (
            <span className="text-xs text-gray-400">(showing first 32 visible)</span>
          )}
        </div>
        <button
          onClick={handleRefresh}
          disabled={queueStatus.isFetching}
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-40"
          title="Refresh"
        >
          <ArrowPathIcon className={`h-3.5 w-3.5 ${queueStatus.isFetching ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {queueStatus.isLoading ? (
        <LoadingWheel />
      ) : quotes.length === 0 ? (
        <p className="text-sm text-gray-500 py-2">
          {count > 0
            ? `${count} message${count !== 1 ? "s" : ""} queued but none are visible yet — they're still within their visibility delay window.`
            : "Queue is empty."}
        </p>
      ) : (
        <div className="overflow-x-auto max-h-72 overflow-y-auto rounded-md border border-gray-100">
          <table className="min-w-full text-xs">
            <thead className="bg-gray-50 sticky top-0">
              <tr className="border-b border-gray-100">
                <th className="text-left px-3 py-2 font-medium text-gray-500">VRM</th>
                <th className="text-left px-3 py-2 font-medium text-gray-500">Contact</th>
                <th className="text-left px-3 py-2 font-medium text-gray-500">Step</th>
                <th className="text-left px-3 py-2 font-medium text-gray-500">Queued</th>
                <th className="text-left px-3 py-2 font-medium text-gray-500">Attempt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 bg-white">
              {quotes.map((q) => (
                <tr key={q.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-mono font-medium text-gray-900">{q.vehicleRegistration ?? "—"}</td>
                  <td className="px-3 py-2 text-gray-600">
                    <div>{q.contactName || "—"}</div>
                    <div className="text-gray-400">{q.contactPhone || ""}</div>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{q.step || "—"}</td>
                  <td className="px-3 py-2 text-gray-400">
                    {new Date(q.enqueuedAtUtc).toLocaleTimeString("en-GB", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="px-3 py-2 text-gray-500">
                    {q.attempt > 1 ? (
                      <span className="text-amber-600 font-medium">#{q.attempt}</span>
                    ) : (
                      "#1"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-gray-400">Auto-refreshes every 30 seconds.</p>
    </div>
  );
}

export default function Functions() {
  const { setInfo } = usePageNameContext();

  useEffect(() => {
    setInfo({ name: "Functions", desc: "Admin utilities for SAV" });
  }, []);

  return (
    <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
      <FunctionCard
        icon={MagnifyingGlassCircleIcon}
        title="Trace VRM"
        description="Everything the widget knows about one registration"
      >
        <TraceVrmCard />
      </FunctionCard>

      <FunctionCard
        icon={QueueListIcon}
        title="Call Centre Queue"
        description="Messages currently waiting to be processed — refreshes every 30s"
      >
        <QueueCard />
      </FunctionCard>

      <FunctionCard
        icon={ArrowPathIcon}
        title="Requeue Today's Quotes"
        description="Push unqueued SAV quotes from today into the call centre queue"
      >
        <RequeueCard />
      </FunctionCard>

      <FunctionCard
        icon={ExclamationTriangleIcon}
        title="Failed Leads"
        description="Leads that never reached their Wilma Core, including ones no longer retried"
      >
        <FailedLeadsCard />
      </FunctionCard>

      <FunctionCard
        icon={DocumentMagnifyingGlassIcon}
        title="Step 8 Without Leads"
        description="SAV quotes that reached step 8 but have no lead attached"
      >
        <Step8Card />
      </FunctionCard>
    </div>
  );
}
